import TimeAgo from "javascript-time-ago";
import React, {
  type ContextType,
  type ReactNode,
  useEffect,
  useMemo,
} from "react";
import { IntlProvider } from "react-intl";
import { DateTimeFormatContext } from "src/app/components/DateTimeFormatContext";
import { LocalizedAppContext } from "src/app/components/LocalizedAppContext";
import {
  AVAILABLE_LANGUAGES,
  type InterfaceLanguageCode,
} from "src/app/consts/AvailableLanguages";
import { useLanguage } from "src/app/hooks/useLanguageFile";
import { useLocalStorage } from "src/shared/hooks/useLocalStorage";
import { languageNames } from "src/shared/i18n/languageNames";

export interface ILocalizedAppProps {
  children?: ReactNode;
}

export const LocalizedApp = ({ children }: ILocalizedAppProps) => {
  const [languageCode, setLanguageCode] =
    useLocalStorage<InterfaceLanguageCode>(
      "languageCode",
      AVAILABLE_LANGUAGES[0],
    );

  const { data: languageFile } = useLanguage(languageCode);
  useEffect(() => {
    if (languageFile?.timeAgoLocale) {
      TimeAgo.addLocale(languageFile.timeAgoLocale);
    }
  }, [languageFile?.timeAgoLocale]);

  const languageStrings = languageFile?.messages;
  const messages = useMemo(() => {
    if (!languageStrings) {
      return {};
    }
    return { ...languageStrings, ...languageNames };
  }, [languageStrings]);
  const localizedAppContextValue = useMemo(
    () => ({
      languageCode,
      setLanguageCode,
      availableLanguages: AVAILABLE_LANGUAGES,
    }),
    [languageCode, setLanguageCode],
  );

  const dateTimeFormatContextValue = useMemo<
    ContextType<typeof DateTimeFormatContext>
  >(() => {
    if (!languageFile) {
      return null;
    }
    return {
      dateTimeFormatter: languageFile.dateTimeFormatter,
      dateFormatter: languageFile.dateFormatter,
    };
  }, [languageFile]);

  if (!languageFile) {
    return null;
  }
  return (
    <LocalizedAppContext.Provider value={localizedAppContextValue}>
      <DateTimeFormatContext.Provider value={dateTimeFormatContextValue}>
        <IntlProvider locale={languageFile.locale} messages={messages}>
          {children}
        </IntlProvider>
      </DateTimeFormatContext.Provider>
    </LocalizedAppContext.Provider>
  );
};
